import React, { useEffect, useState } from 'react';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, DollarSign, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const money = (c) => `$${(c / 100).toFixed(2)}`;

function OptionGroup({ title, options, selected, onPick }) {
  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-[0.2em] text-[#C6A14A]/80">{title}</p>
      {options.map((o) => (
        <button key={o.code} type="button" onClick={() => onPick(o)} data-testid={`package-option-${o.code}`}
          className={`w-full flex items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors duration-200 ${selected === o.code ? 'border-[#C6A14A] bg-[#C6A14A]/10' : 'border-white/10 hover:border-white/30'}`}>
          <span>
            <span className="block text-white text-sm">{o.name}</span>
            <span className="block text-xs text-[#E8E4DE]/50">{o.description}</span>
          </span>
          <span className="flex items-center gap-2 text-[#C6A14A] text-sm">
            {money(o.price_cents)}{selected === o.code && <Check className="h-4 w-4" />}
          </span>
        </button>
      ))}
    </div>
  );
}

export function ChangePackageDialog({ orderId, open, onOpenChange, onChanged }) {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [price, setPrice] = useState('');
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    authFetch(`${API_BASE}/api/admin/orders/${orderId}/package-options`).then((r) => r.json()).then((d) => {
      setOptions(d.options || []);
      const cur = (d.options || []).find((o) => o.code === d.current);
      setSelected(cur?.code || null);
      setPrice(cur ? (cur.price_cents / 100).toFixed(2) : '');
    });
  }, [open, orderId]);

  const pick = (o) => { setSelected(o.code); setPrice((o.price_cents / 100).toFixed(2)); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/change-package`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package: selected, price_cents: Math.round(parseFloat(price) * 100), notify_client: notify }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not change the package');
      toast.success(`Package changed to ${data.package_name}${notify ? (data.emailed ? ' · client emailed' : ' · email not sent') : ''}`);
      onOpenChange(false);
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const valid = selected && parseFloat(price) > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1724] border-white/10 text-white z-[1000] max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="change-package-dialog">
        <DialogHeader>
          <DialogTitle>Change package</DialogTitle>
          <DialogDescription className="text-[#E8E4DE]/60">Pick the package to invoice. Locked once an invoice is created.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <OptionGroup title="Real Estate" options={options.filter((o) => o.category === 'Real Estate')} selected={selected} onPick={pick} />
          <OptionGroup title="Vacant Land" options={options.filter((o) => o.category === 'Vacant Land')} selected={selected} onPick={pick} />
          <div>
            <Label className="text-[#E8E4DE]/80 text-sm">Package price</Label>
            <div className="relative mt-2">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#E8E4DE]/40" />
              <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
                className="pl-9 bg-[#050816]/50 border-white/10 text-white" data-testid="change-package-price-input" />
            </div>
            <p className="text-xs text-[#E8E4DE]/40 mt-1">Add-ons already on the order stay as they are.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#E8E4DE]/80">
            <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="change-package-notify-checkbox" />
            Email the client about this change
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="change-package-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={!valid || saving} className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]" data-testid="change-package-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save package'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
